import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  IconButton,
  Modal,
  MenuItem,
  Divider,
  Typography,
  Checkbox,
  useMediaQuery,
  TextField,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { updateLocalTheme } from "../redux/slices/themeSlice";
import {
  ApiKey,
  SettingsModalInterface,
} from "../assets/interfaces/interfaces";
import { AppDispatch } from "../redux/store";
import { fetchDeleteUser } from "../redux/slices/deleteUserSlice";
import { useDeleteAllChatsMutation, usePaginatedChats } from "./Sidebar/hooks";
import { AddCircleRounded, ChevronLeftRounded, CloseRounded, CodeRounded, EditNotificationsRounded, SettingsRounded } from "@mui/icons-material";
import ToggleButtonsGroup from "./Shared/ToggleButtonsGroup";
import DeleteModal from "./Shared/DeleteModal";
import classNames from "classnames";
import { updateNotifPreferences } from "../redux/slices/userSlice";
import { showNotification } from "../redux/slices/notificationSlice";
import { theme } from "../theme";
import { isEmailDomainWhitelisted, formatDateToMMDDYYYY } from "../helpers";
import ApiKeyModal from "./Sidebar/ApiKeyModal";
import { deleteApiKey, fetchApiKeys } from "../redux/slices/apiKeysSlice";

const allSettingsMenuItems = [
  {
    id: 1,
    label: "General",
    icon: <SettingsRounded className="!h-5 !w-5" />,
  },
  {
    id: 2,
    label: "Notifications",
    icon: <EditNotificationsRounded className="!h-5 !w-5" />,
  },
  {
    id: 3,
    label: "Developer Tools",
    icon: <CodeRounded className="!h-5 !w-5" />,
  },
];

const SettingsModal: React.FC<SettingsModalInterface> = ({
  open,
  setOpen,
  handleProfileMenuClose,
  clearAllChatData,
  handleSignOut,
  initialActiveMenu,
}) => {
  const handleClose = () => {
    setOpen(false);
    handleProfileMenuClose();
  };
  const accountDeletedStatus = useSelector(
    (state: { deleteUser }) => state?.deleteUser?.status,
  );
  const { localTheme } = useSelector((state: { theme }) => state.theme);
  const dispatch: AppDispatch = useDispatch();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteUser, setDeleteUser] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeMenuItem, setActiveMenuItem] = useState(isMobile ? null : 1);
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const [apiKeyToDelete, setApiKeyToDelete] = useState<ApiKey | null>(null);
  const hasFetchedApiKeysRef = useRef(false);

  const user = useSelector((state: { auth }) => state.auth.currentUser);
  const { notifications, status } = useSelector((state: { user }) => state.user);
  const { apiKeys, isLoading: apiKeysLoading } = useSelector((state: { apiKeys }) => state.apiKeys);

  // Filter settings menu items based on email domain whitelist
  const settingsMenuItems = allSettingsMenuItems.filter((item) => {
    if (item.id === 3) {
      return isEmailDomainWhitelisted(user?.email);
    }
    return true;
  });

  useEffect(() => {
    if (initialActiveMenu) {
      setActiveMenuItem(initialActiveMenu);
    }
  }, [initialActiveMenu]);

  // Fetch API keys when Developer Tools tab is accessed and modal is open (only if not already loaded)
  useEffect(() => {
    if (
      open
      && activeMenuItem === 3
      && isEmailDomainWhitelisted(user?.email)
      && !apiKeysLoading
      && !hasFetchedApiKeysRef.current
    ) {
      hasFetchedApiKeysRef.current = true;
      dispatch(fetchApiKeys());
    }
  }, [open, activeMenuItem, user?.email, dispatch, apiKeysLoading]);

  // assume both true if notifications is undefined
  const normalizedNotifs
  = status === "succeeded"
    ? notifications?.email ?? { marketing: true, account: true }
    : { marketing: false, account: false };
  const userId = user?.userId || "";

  const [userNotifs, setUserNotifs] = useState(normalizedNotifs);

  useEffect(() => {
    setUserNotifs(normalizedNotifs);
  }, [notifications]);

  const handleUpdateEmailPrefs = async (field: "account" | "marketing") => {
    const updated = { ...userNotifs, [field]: !userNotifs[field] };
    setUserNotifs(updated);
    try {
      await dispatch(updateNotifPreferences(updated));
      const isSubscribed = updated[field];
      dispatch(showNotification ({ message: `You’ve successfully ${isSubscribed ? "subscribed" : "unsubscribed"} to ${field} emails.`, severity: "success", horizontal: "right" }));
    }
    catch (err) {
      console.error(err);
      dispatch(showNotification ({ message: "Changes not saved, please try again.", severity: "error", horizontal: "right" }));
    }
  };

  const handleDeleteUser = () => {
    dispatch(fetchDeleteUser());
    setConfirmOpen(false);
    setDeleteUser(false);
  };

  useEffect(() => {
    if (accountDeletedStatus === "succeeded") {
      handleSignOut();
    }
  }, [accountDeletedStatus]);

  const deleteAllChats = useDeleteAllChatsMutation();

  const handleDeleteAllChats = async () => {
    try {
      deleteAllChats.mutate({ userId });
      clearAllChatData();
    }
    catch (error) {
      console.error("Failed to delete all chats:", error);
    }
  };

  const { data: chatHistory } = usePaginatedChats({ userId: user?.userId });

  const handleDeleteApiKey = async () => {
    try {
      await dispatch(deleteApiKey(apiKeyToDelete.id)).unwrap();
    }
    catch (error) {
      console.error("Failed to delete API key:", error);
      dispatch(showNotification({ message: "Failed to delete API key. Please try again.", severity: "error", horizontal: "right" }));
    }
    finally {
      setApiKeyToDelete(null);
    }
  };

  return (
    <>
      {!confirmOpen && !apiKeyModalOpen && (
        <Modal open={open} onClose={handleClose}>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="shadow w-modal-mobile sm:w-modal-md lg:w-modal-lg sm:!h-[510px] text-mui-black-87 dark:text-white">
              <div className="w-full sm:flex justify-between h-full">

                {(!isMobile || !activeMenuItem) && (
                  <section className="w-full rounded-lg flex flex-col py-6 sm:w-[186px] bg-white dark:bg-background/paper-elevation-24 sm:rounded-s-lg sm:rounded-e-none">
                    <Typography variant="h6/medium-500" className="px-4 pb-2 sm:pb-5 items-center">Settings</Typography>
                    <div className="flex flex-col gap-y-1 pt-2">
                      {settingsMenuItems.map(setting => (
                        <MenuItem
                          key={setting.id}
                          onClick={() => setActiveMenuItem(setting.id)}
                          selected={setting.id === activeMenuItem}
                          className={classNames({
                            "!bg-tgpt-secondary-states-hover !rounded-[4px]": setting.id === activeMenuItem,
                          })}
                        >
                          <div className="py-1 flex items-center gap-x-[12px]">
                            <div className="!text-action-active ">{setting.icon}</div>
                            <div className="dark:text-white">{setting.label}</div>
                          </div>
                        </MenuItem>
                      ))}
                    </div>
                  </section>
                )}

                {activeMenuItem && (
                  <div className="rounded-lg dark:bg-background/paper-elevation-16 bg-white py-6 px-4 flex flex-col sm:rounded-e-lg sm:rounded-s-none flex-1">
                    <div className="relative sm:pb-6">
                      {isMobile && (
                        <IconButton
                          onClick={() => setActiveMenuItem(null)}
                          size="small"
                          sx={{ position: "absolute", top: 0, left: 8 }}
                        >
                          <ChevronLeftRounded className="!fill-action-active !w-5 !h-5" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={handleClose}
                        className="cross-icon"
                        sx={{ position: "absolute", top: 0, right: 8 }}
                      >
                        <CloseRounded className="!fill-action-active !w-5 !h-5" />
                      </IconButton>
                    </div>

                    {activeMenuItem === 1 && (
                      <section className="flex flex-col gap-y-6">
                        <Typography variant="h6/medium-500" className={isMobile ? "pl-10" : ""}>General</Typography>
                        <div className="flex flex-col gap-y-2 items-start">
                          <Typography variant="body2/regular-400">Theme</Typography>
                          <ToggleButtonsGroup
                            stateVar={localTheme}
                            setter={(newValue: "light" | "dark" | "system") =>
                              dispatch(updateLocalTheme(newValue))}
                            options={["system", "dark", "light"]}
                          />
                        </div>
                        <Divider />
                        <div className="flex justify-between items-center">
                          <Typography variant="body2/regular-400">Delete all chats</Typography>
                          <Button
                            disabled={!chatHistory?.pages?.length}
                            onClick={() => {
                              setOpen(false); // Close the first modal
                              setConfirmOpen(true); // Open the second modal after a short delay
                            }}
                            variant="contained"
                            className="!bg-error-main !normal-case"
                            size="large"
                          >
                            Delete
                          </Button>
                        </div>
                      </section>
                    )}

                    {activeMenuItem === 2 && (
                      <section className="flex flex-col gap-y-4">
                        <Typography variant="body1/regular-400" className={`w-[247px] sm:w-full ${isMobile ? "pl-10" : ""}`}>Select the type of emails you wish to receive</Typography>
                        <div className="flex items-center gap-x-2">
                          <Checkbox
                            checked={userNotifs.account}
                            className={
                              classNames("!p-0 text-mui-black-60 dark:text-mui-white-70",
                                {
                                  "!text-tgpt-secondary-main": userNotifs.account,
                                },
                              )
                            }
                            onChange={() => handleUpdateEmailPrefs("account")}
                          />
                          <Typography variant="body2/regular-400" className="text-mui-black-60 dark:text-mui-white-70">Product and Feature Announcements</Typography>
                        </div>
                        <div className="flex items-center gap-x-2">
                          <Checkbox
                            checked={userNotifs.marketing}
                            className={
                              classNames("!p-0 text-mui-black-60 dark:text-mui-white-70",
                                {
                                  "!text-tgpt-secondary-main": userNotifs.marketing,
                                },
                              )
                            }
                            onChange={() => handleUpdateEmailPrefs("marketing")}
                          />
                          <Typography variant="body2/regular-400" className="text-mui-black-60 dark:text-mui-white-70">Offers and Promotions</Typography>
                        </div>
                        <Divider />
                        <Typography variant="body2/regular-400" className="dark:text-mui-white-70 pt-[9px]">Feature-specific notifications can be managed within each feature.</Typography>
                      </section>
                    )}
                    {activeMenuItem === 3 && (
                      <section className="flex flex-col gap-y-4 max-h-[510px] overflow-y-auto custom-scrollbar box-content me-[-12px] pe-[8px] pb-2">
                        <Typography variant="body1/regular-400" className={isMobile ? "pl-10" : ""}>Manage API Key(s)</Typography>
                        {apiKeys.length > 0 && apiKeys.map(key => (
                          <div className="p-4 rounded-lg bg-tgpt-secondary-accentLight flex flex-col gap-y-2" key={key.id}>
                            <div className="flex gap-x-4">
                              <Typography variant="caption/regular-400" className="min-w-[80px] sm:min-w-[99px] text-left text-mui-black-60 dark:text-mui-white-70">Name</Typography>
                              <Typography variant="body2/regular-400">{key.name}</Typography>
                            </div>
                            <div className="flex gap-x-4">
                              <Typography variant="caption/regular-400" className="min-w-[80px] sm:min-w-[99px] text-left text-mui-black-60 dark:text-mui-white-70">Description</Typography>
                              <Typography variant="body2/regular-400">{key.description}</Typography>
                            </div>
                            <div className="flex gap-x-4">
                              <Typography variant="caption/regular-400" className="min-w-[80px] sm:min-w-[99px] text-left text-mui-black-60 dark:text-mui-white-70">API Key</Typography>
                              <Typography variant="body2/regular-400">{key.prefix}</Typography>
                            </div>
                            <div className="flex gap-x-4">
                              <Typography variant="caption/regular-400" className="min-w-[80px] sm:min-w-[99px] text-left text-mui-black-60 dark:text-mui-white-70">Date created</Typography>
                              <Typography variant="body2/regular-400">{formatDateToMMDDYYYY(key.created_at)}</Typography>
                            </div>
                            <Button variant="contained" className="!bg-error-main !normal-case items-center gap-x-2 w-[75px] self-end" onClick={() => setApiKeyToDelete(key)}>Delete</Button>
                          </div>
                        ))}
                        <Button
                          variant="contained"
                          className="!bg-tgpt-secondary-accentLight !normal-case items-center gap-x-2 !text-tgpt-secondary-main"
                          onClick={() => setApiKeyModalOpen(true)}
                        >
                          <AddCircleRounded className="!h-5 !w-5 !fill-tgpt-secondary-main" />
                          Generate new
                        </Button>
                      </section>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}
      <DeleteModal
        open={confirmOpen}
        handleDelete={deleteUser ? handleDeleteUser : handleDeleteAllChats}
        handleCloseWarning={() => {
          setDeleteUser(false);
          setConfirmOpen(false);
        }}
        mainText="Clear Conversation History"
        subText="Are you sure you would like to delete all your chats? The deletion is irreversible."
      />

      <DeleteModal
        open={!!apiKeyToDelete}
        handleDelete={handleDeleteApiKey}
        handleCloseWarning={() => {
          setApiKeyToDelete(null);
        }}
        widthClassName="w-modal-md"
        mainText="Delete API Key?"
        subText="This API key will immediately be disabled. API requests made using this key will be rejected, which could cause any systems still depending on it to break. Once revoked, you’ll no longer be able to view or modify this API key."
        additionalContent={(
          <TextField
            className="outlined-input !w-fit"
            value={apiKeyToDelete?.prefix}
            fullWidth={false}
            InputProps={{ readOnly: true }}
          />
        )}
        isDeleting={apiKeysLoading}
      />
      <ApiKeyModal open={apiKeyModalOpen} setOpen={setApiKeyModalOpen} />
    </>
  );
};
export default SettingsModal;
